import React, { useCallback, useEffect, useRef, useState } from 'react';
import { adminAPI } from '../../services/api';
import ArchiveWorkspaceModal from '../../components/admin/ArchiveWorkspaceModal';

const statusLabel = (w) => {
    if (!w.archived_at) return 'Live';
    return w.archive_source === 'workspace' ? 'Archived — by admin' : 'Archived — with account';
};

const AdminWorkspacesPanel = ({ account }) => {
    const [workspaces, setWorkspaces] = useState([]);
    const [archiveTarget, setArchiveTarget] = useState(null);
    const [error, setError] = useState('');

    // Tracks which account the panel currently wants data for. A `load()` call
    // closes over the account id it was fired for; if the account changes (or
    // this component is reused across accounts without a fresh key) before that
    // call's request resolves, the id it captured no longer matches this ref by
    // the time it resumes, and it must not apply its response — otherwise a slow
    // response for the old account can repopulate the new account's view, and
    // its archive/restore buttons would then act on the wrong account's rows.
    const currentAccountId = useRef(account.id);

    const load = useCallback(async () => {
        const requestedFor = account.id;
        setError('');
        try {
            const data = await adminAPI.getAccountWorkspaces(requestedFor);
            if (currentAccountId.current !== requestedFor) return; // stale: account changed since this request was fired
            setWorkspaces(data);
        } catch {
            if (currentAccountId.current !== requestedFor) return;
            setError('Could not load workspaces.');
        }
    }, [account.id]);

    useEffect(() => {
        currentAccountId.current = account.id;
        load();
    }, [account.id, load]);

    const liveCount = workspaces.filter((w) => !w.archived_at).length;

    const handleRestore = async (w) => {
        setError('');
        try {
            await adminAPI.restoreWorkspace(w.id);
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Could not restore this workspace.');
        }
    };

    return (
        <div className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-3 text-sm font-semibold uppercase text-muted-foreground">Workspaces</h3>
            {error && <div className="mb-2 text-sm text-destructive">{error}</div>}
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left text-xs uppercase text-muted-foreground">
                        <th className="py-1">Name</th><th>Status</th><th>Members</th><th />
                    </tr>
                </thead>
                <tbody>
                    {workspaces.map((w) => (
                        <tr key={w.id} className="border-t border-border">
                            <td className="py-2 text-foreground">{w.name}</td>
                            <td title={w.archive_reason || undefined}>{statusLabel(w)}</td>
                            <td>{w.member_count}</td>
                            <td className="text-right">
                                {w.archived_at ? (
                                    <button type="button" aria-label={`Restore ${w.name}`}
                                            disabled={w.archive_source !== 'workspace'}
                                            title={w.archive_source !== 'workspace' ? 'Archived with its account — restore the account instead' : undefined}
                                            onClick={() => handleRestore(w)}
                                            className="rounded-md border border-border px-2 py-1 text-xs disabled:opacity-50">
                                        Restore
                                    </button>
                                ) : (
                                    <button type="button" aria-label={`Archive ${w.name}`}
                                            onClick={() => setArchiveTarget(w)}
                                            className="rounded-md border border-border px-2 py-1 text-xs">
                                        Archive
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {archiveTarget && (
                <ArchiveWorkspaceModal
                    workspace={archiveTarget}
                    isLastLive={liveCount === 1}
                    onClose={() => setArchiveTarget(null)}
                    onArchived={() => { setArchiveTarget(null); load(); }}
                />
            )}
        </div>
    );
};

export default AdminWorkspacesPanel;
